import React, { useState } from "react";
import { Link } from "react-router-dom";

import PageHero from "../components/PageHero";
import { ArrowIcon, CloseIcon, CopyIcon, PlusIcon } from "../components/Icons";

const MAX_CALCS = 3;

const makeId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Keep inputs as TEXT so user can type freely
const defaultCalc = (id = makeId()) => ({
  id,
  name: "Scenario",
  loanAmount: "650000",
  deposit: "130000",
  interestRate: "6.99",
  termYears: "30",
  frequency: "weekly", // weekly | fortnightly | monthly
  repaymentType: "principal_interest", // principal_interest | interest_only
  interestOnlyYears: "5",
  extraPayment: "0",
});

function parseNum(val) {
  if (val === null || val === undefined) return NaN;
  if (typeof val === "number") return val;
  const cleaned = String(val).replace(/[^0-9.-]/g, ""); // removes commas, $ etc.
  if (cleaned === "" || cleaned === "-" || cleaned === "." || cleaned === "-.") return NaN;
  return Number(cleaned);
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return NaN;
  return Math.min(max, Math.max(min, n));
}

function normalizeMoney(val, min = 0, max = 100000000) {
  const n = clamp(parseNum(val), min, max);
  if (!Number.isFinite(n)) return String(min);
  return String(Math.round(n));
}

function normalizeDecimal(val, min, max, decimals = 2) {
  const n = clamp(parseNum(val), min, max);
  if (!Number.isFinite(n)) return (min || 0).toFixed(decimals);
  return n.toFixed(decimals);
}

function normalizeInt(val, min, max) {
  const n = clamp(parseNum(val), min, max);
  if (!Number.isFinite(n)) return String(min);
  return String(Math.round(n));
}

function fmtMoney(n) {
  if (!Number.isFinite(n)) return "$0";
  return n.toLocaleString("en-NZ", { style: "currency", currency: "NZD" });
}

function fmtPct(n) {
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2)}%`;
}

function getPeriodsPerYear(freq) {
  if (freq === "weekly") return 52;
  if (freq === "fortnightly") return 26;
  return 12;
}

function calcMortgage(calc) {
  const price = Math.max(0, parseNum(calc.loanAmount) || 0);
  const deposit = Math.max(0, parseNum(calc.deposit) || 0);
  const principal = Math.max(0, price - deposit);

  const interestRate = Math.max(0, parseNum(calc.interestRate) || 0);
  const termYears = Math.max(1, parseNum(calc.termYears) || 1);
  const extraPayment = Math.max(0, parseNum(calc.extraPayment) || 0);

  const ppy = getPeriodsPerYear(calc.frequency);
  const r = (interestRate / 100) / ppy;
  const nTotal = Math.max(1, Math.round(termYears * ppy));

  if (principal <= 0) {
    return {
      principal: 0,
      payment: 0,
      totalInterest: 0,
      totalPaid: 0,
      payoffPeriods: 0,
      schedulePreview: [],
    };
  }

  const piBase =
    r === 0
      ? principal / nTotal
      : (principal * r) / (1 - Math.pow(1 + r, -nTotal));

  const ioPayment = principal * r;

  const repaymentType = calc.repaymentType;
  const interestOnlyYears = Math.max(0, parseNum(calc.interestOnlyYears) || 0);

  const ioPeriods =
    repaymentType === "interest_only"
      ? Math.min(nTotal, Math.round(interestOnlyYears * ppy))
      : 0;

  let balance = principal;
  let totalInterest = 0;
  let totalPaid = 0;
  let payoffPeriods = 0;

  let basePayment = repaymentType === "interest_only" ? ioPayment : piBase;

  const schedulePreview = [];
  const maxSim = 200 * ppy;

  for (let i = 1; i <= Math.min(nTotal, maxSim); i++) {
    const isIOPhase = repaymentType === "interest_only" && i <= ioPeriods;

    if (repaymentType === "interest_only" && i === ioPeriods + 1) {
      const remainingPeriods = Math.max(1, nTotal - ioPeriods);
      basePayment =
        r === 0
          ? balance / remainingPeriods
          : (balance * r) / (1 - Math.pow(1 + r, -remainingPeriods));
    }

    const interest = balance * r;
    let paymentThisPeriod = (isIOPhase ? balance * r : basePayment) + extraPayment;

    if (paymentThisPeriod < interest) paymentThisPeriod = interest;

    let principalPaid = paymentThisPeriod - interest;

    if (principalPaid > balance) {
      principalPaid = balance;
      paymentThisPeriod = interest + principalPaid;
    }

    balance -= principalPaid;
    totalInterest += interest;
    totalPaid += paymentThisPeriod;

    if (i <= 6) {
      schedulePreview.push({
        period: i,
        payment: paymentThisPeriod,
        interest,
        principal: principalPaid,
        balance: Math.max(0, balance),
      });
    }

    if (balance <= 0.0001) {
      payoffPeriods = i;
      break;
    }
  }

  if (payoffPeriods === 0) payoffPeriods = nTotal;

  schedulePreview.push({
    period: payoffPeriods,
    finalRow: true,
    balance: 0,
  });

  const shownPayment =
    (repaymentType === "interest_only" ? ioPayment : piBase) + extraPayment;

  return {
    principal,
    payment: shownPayment,
    totalInterest,
    totalPaid,
    payoffPeriods,
    schedulePreview,
  };
}

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
];

const REPAYMENT_OPTIONS = [
  { value: "principal_interest", label: "Principal & interest" },
  { value: "interest_only", label: "Interest-only" },
];

const FREQUENCY_NOUN = {
  weekly: "week",
  fortnightly: "fortnight",
  monthly: "month",
};

function SummaryStat({ label, value, hint }) {
  return (
    <div className="calc-stat">
      <div className="calc-stat-label">
        {label}
        {hint ? (
          <span className="calc-hint" title={hint} aria-label={hint}>
            ?
          </span>
        ) : null}
      </div>
      <div className="calc-stat-value">{value}</div>
    </div>
  );
}

function Field({ label, htmlFor, wide = false, children }) {
  return (
    <div className={`calc-field ${wide ? "calc-field-wide" : ""}`}>
      <label className="calc-field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

// Text input with a fixed unit on one side ("$", "%", "years").
function AffixInput({ id, prefix, suffix, ...inputProps }) {
  return (
    <div className="calc-input-wrap">
      {prefix && <span className="calc-affix">{prefix}</span>}
      <input id={id} type="text" className="calc-input" {...inputProps} />
      {suffix && <span className="calc-affix">{suffix}</span>}
    </div>
  );
}

// Two or three choices read faster as a toggle than as a dropdown.
function Segmented({ label, name, options, value, onChange }) {
  return (
    <fieldset className="calc-field calc-field-wide calc-segment-field">
      <legend className="calc-field-label">{label}</legend>
      <div className="calc-segment">
        {options.map((option) => (
          <label
            key={option.value}
            className={`calc-segment-option ${
              value === option.value ? "active" : ""
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function CalculatorPage() {
  const [calcs, setCalcs] = useState([{ ...defaultCalc(), name: "Scenario A" }]);

  // Only updates when the user clicks Calculate
  const [computed, setComputed] = useState({}); // { [id]: result }

  const updateCalc = (id, patch) => {
    setCalcs((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    // IMPORTANT: do NOT compute here (no real-time results)
  };

  const doCalculate = (id) => {
    setCalcs((prev) => {
      const next = prev.map((c) => {
        if (c.id !== id) return c;

        const normalized = {
          loanAmount: normalizeMoney(c.loanAmount),
          deposit: normalizeMoney(c.deposit),
          interestRate: normalizeDecimal(c.interestRate, 0, 30, 2),
          termYears: normalizeInt(c.termYears, 1, 40),
          extraPayment: normalizeMoney(c.extraPayment, 0, 1000000),
          interestOnlyYears: normalizeInt(
            c.interestOnlyYears,
            0,
            Number(parseNum(c.termYears) || 40)
          ),
        };

        return { ...c, ...normalized };
      });

      const calculated = next.find((x) => x.id === id);
      setComputed((prevRes) => ({
        ...prevRes,
        [id]: calcMortgage(calculated),
      }));

      return next;
    });
  };

  const addCalc = () => {
    setCalcs((prev) => {
      if (prev.length >= MAX_CALCS) return prev;
      const nextIndex = prev.length + 1;
      return [
        ...prev,
        { ...defaultCalc(), name: `Scenario ${String.fromCharCode(64 + nextIndex)}` },
      ];
    });
  };

  const removeCalc = (id) => {
    setCalcs((prev) => prev.filter((c) => c.id !== id));
    setComputed((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const copyCalc = (id) => {
    setCalcs((prev) => {
      if (prev.length >= MAX_CALCS) return prev;
      const src = prev.find((c) => c.id === id);
      if (!src) return prev;
      const nextIndex = prev.length + 1;
      return [
        ...prev,
        { ...src, id: makeId(), name: `Scenario ${String.fromCharCode(64 + nextIndex)}` },
      ];
    });
  };

  const atLimit = calcs.length >= MAX_CALCS;

  return (
    <div className="calc-page">
      <PageHero
        kicker="Calculator"
        title="Mortgage calculator"
        subtitle="Work out repayments and put up to three scenarios side by side. Nothing recalculates until you press Calculate."
        overlap
      >
        <div className="calc-toolbar">
          <button
            className="pill-btn pill-btn-primary"
            onClick={addCalc}
            disabled={atLimit}
            type="button"
          >
            <PlusIcon />
            Add comparison
          </button>
          <span className="calc-toolbar-note">
            {calcs.length} of {MAX_CALCS} scenarios
          </span>
        </div>
      </PageHero>

      <section className="calc-section">
        <div className={`calc-grid calc-grid-${Math.min(calcs.length, 3)}`}>
          {calcs.map((c) => {
            const r = computed[c.id]; // only exists after Calculate
            const ppy = getPeriodsPerYear(c.frequency);
            const yearsToPayoff = r?.payoffPeriods ? r.payoffPeriods / ppy : 0;
            const per = FREQUENCY_NOUN[c.frequency];
            const fieldId = (name) => `${c.id}-${name}`;
            const interestShare =
              r && r.principal + r.totalInterest > 0
                ? r.totalInterest / (r.principal + r.totalInterest)
                : 0;

            return (
              <div key={c.id} className="calc-card">
                <div className="calc-card-top">
                  <input
                    className="calc-scenario-name"
                    value={c.name}
                    onChange={(e) => updateCalc(c.id, { name: e.target.value })}
                    aria-label="Scenario name"
                  />

                  <div className="calc-card-tools">
                    <button
                      className="calc-chip"
                      type="button"
                      onClick={() => copyCalc(c.id)}
                      disabled={atLimit}
                      title="Duplicate this scenario"
                    >
                      <CopyIcon size={15} />
                      Copy
                    </button>
                    {calcs.length > 1 && (
                      <button
                        className="calc-chip danger"
                        type="button"
                        onClick={() => removeCalc(c.id)}
                        title="Remove this scenario"
                      >
                        <CloseIcon size={15} />
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Enter key triggers Calculate */}
                <form
                  className="calc-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    doCalculate(c.id);
                  }}
                >
                  <div className="calc-inputs">
                    <div className="calc-form-grid">
                      <Field label="Property price" htmlFor={fieldId("price")}>
                        <AffixInput
                          id={fieldId("price")}
                          prefix="$"
                          inputMode="numeric"
                          value={c.loanAmount}
                          placeholder="650,000"
                          onChange={(e) => updateCalc(c.id, { loanAmount: e.target.value })}
                        />
                      </Field>

                      <Field label="Deposit" htmlFor={fieldId("deposit")}>
                        <AffixInput
                          id={fieldId("deposit")}
                          prefix="$"
                          inputMode="numeric"
                          value={c.deposit}
                          placeholder="130,000"
                          onChange={(e) => updateCalc(c.id, { deposit: e.target.value })}
                        />
                      </Field>

                      <Field label="Interest rate" htmlFor={fieldId("rate")}>
                        <AffixInput
                          id={fieldId("rate")}
                          suffix="% p.a."
                          inputMode="decimal"
                          value={c.interestRate}
                          placeholder="6.99"
                          onChange={(e) => updateCalc(c.id, { interestRate: e.target.value })}
                        />
                      </Field>

                      <Field label="Term" htmlFor={fieldId("term")}>
                        <AffixInput
                          id={fieldId("term")}
                          suffix="years"
                          inputMode="numeric"
                          value={c.termYears}
                          placeholder="30"
                          onChange={(e) => updateCalc(c.id, { termYears: e.target.value })}
                        />
                      </Field>

                      <Segmented
                        label="Repayment frequency"
                        name={fieldId("frequency")}
                        options={FREQUENCY_OPTIONS}
                        value={c.frequency}
                        onChange={(frequency) => updateCalc(c.id, { frequency })}
                      />

                      <Segmented
                        label="Repayment type"
                        name={fieldId("type")}
                        options={REPAYMENT_OPTIONS}
                        value={c.repaymentType}
                        onChange={(repaymentType) => updateCalc(c.id, { repaymentType })}
                      />

                      {c.repaymentType === "interest_only" && (
                        <Field label="Interest-only period" htmlFor={fieldId("io")}>
                          <AffixInput
                            id={fieldId("io")}
                            suffix="years"
                            inputMode="numeric"
                            value={c.interestOnlyYears}
                            placeholder="5"
                            onChange={(e) => updateCalc(c.id, { interestOnlyYears: e.target.value })}
                          />
                        </Field>
                      )}

                      <Field label={`Extra repayments per ${per}`} htmlFor={fieldId("extra")}>
                        <AffixInput
                          id={fieldId("extra")}
                          prefix="$"
                          inputMode="numeric"
                          value={c.extraPayment}
                          placeholder="50"
                          onChange={(e) => updateCalc(c.id, { extraPayment: e.target.value })}
                        />
                      </Field>
                    </div>

                    <button type="submit" className="pill-btn pill-btn-primary calc-submit">
                      Calculate
                    </button>
                  </div>

                  {/* RESULTS SHOW ONLY AFTER CALCULATE */}
                  <div className="calc-results" aria-live="polite">
                    {!r ? (
                      <div className="calc-empty">
                        <span className="calc-empty-figure">$ —</span>
                        <p>
                          Press <b>Calculate</b> to see your repayments.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="calc-headline">
                          <span className="calc-headline-label">
                            Estimated repayment per {per}
                          </span>
                          <span className="calc-headline-value">
                            {fmtMoney(r.payment)}
                          </span>
                          <span className="calc-headline-note">
                            {parseNum(c.extraPayment) > 0
                              ? "Includes your extra repayments"
                              : `At ${fmtPct(parseNum(c.interestRate) || 0)} over ${c.termYears} years`}
                          </span>
                        </div>

                        {/* How the total splits between the loan and the interest */}
                        <div className="calc-split">
                          <div className="calc-split-bar" aria-hidden="true">
                            <span
                              className="calc-split-principal"
                              style={{ width: `${(1 - interestShare) * 100}%` }}
                            />
                            <span
                              className="calc-split-interest"
                              style={{ width: `${interestShare * 100}%` }}
                            />
                          </div>
                          <div className="calc-split-legend">
                            <span>
                              <i className="calc-dot calc-dot-principal" />
                              Loan {fmtMoney(r.principal)}
                            </span>
                            <span>
                              <i className="calc-dot calc-dot-interest" />
                              Interest {fmtMoney(r.totalInterest)}
                            </span>
                          </div>
                        </div>

                        <div className="calc-results-grid">
                          <SummaryStat
                            label="Loan amount"
                            value={fmtMoney(r.principal)}
                            hint="Property price, less your deposit"
                          />
                          <SummaryStat label="Total interest" value={fmtMoney(r.totalInterest)} />
                          <SummaryStat label="Total paid" value={fmtMoney(r.totalPaid)} />
                          <SummaryStat
                            label="Paid off in"
                            value={yearsToPayoff ? `${yearsToPayoff.toFixed(1)} years` : "N/A"}
                          />
                        </div>

                        <details className="calc-preview">
                          <summary>Repayment preview</summary>

                          <div className="calc-table-wrap">
                            <table className="calc-table">
                              <thead>
                                <tr>
                                  <th>Period</th>
                                  <th>Payment</th>
                                  <th>Interest</th>
                                  <th>Principal</th>
                                  <th>Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.schedulePreview ?? []).map((row, idx) =>
                                  row.finalRow ? (
                                    <tr className="final" key={`final-${idx}`}>
                                      <td>Final ({row.period})</td>
                                      <td>–</td>
                                      <td>–</td>
                                      <td>–</td>
                                      <td>{fmtMoney(0)}</td>
                                    </tr>
                                  ) : (
                                    <tr key={idx}>
                                      <td>{row.period}</td>
                                      <td>{fmtMoney(row.payment)}</td>
                                      <td>{fmtMoney(row.interest)}</td>
                                      <td>{fmtMoney(row.principal)}</td>
                                      <td>{fmtMoney(row.balance)}</td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      </>
                    )}
                  </div>
                </form>
              </div>
            );
          })}
        </div>

        <p className="calc-disclaimer">
          Estimates only. Interest rates, fees, and actual bank calculations may
          differ.
        </p>
      </section>

      <section className="cta-band-section">
        <div className="cta-band">
          <div className="cta-band-text">
            <h2>Want the real number?</h2>
            <p>
              A calculator gives you a ballpark. We can tell you what a lender
              will actually offer.
            </p>
          </div>
          <div className="cta-band-actions">
            <Link to="/contact" className="pill-btn pill-btn-light">
              Book a free chat
              <ArrowIcon />
            </Link>
            <a href="tel:+6492176765" className="cta-band-phone">
              or call 09-2176765
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
